import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  Scan,
  ScanReviewGroup,
  StoreCustomer,
  StoreNotFound,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { errorText } from "../primitives/errors";
import { READ_FAILED_ON_THE_WAY } from "../primitives/review-row";
import { shopEntryId } from "../primitives/workspace";
import type { CustomersRepository } from "../repositories/customers-repository";
import type { BackendApi } from "./api";

/**
 * Fetches a counter's customers into the repository, those whose only scans are ones no card was recognised in
 * among them. Single flight for one counter, unless `fresh` asks for a read started after something the one on its
 * way could not see. A read that throws is a failed read, then thrown on.
 */
export async function loadCustomers(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  customersRepository: CustomersRepository;
  fresh?: boolean;
}): Promise<void> {
  const repo = o.customersRepository;
  const entry = shopEntryId(o.shopId, o.locationId);
  if (repo.status.kind === "loading" && repo.entry === entry && !o.fresh) return;
  const asked = repo.setLoading(entry);
  const r = await o.authHandler
    .callApi((ctx) => o.client.scans.customers(ctx, entry, { enableNoMatch: true }))
    .catch((thrown: unknown) => {
      repo.setError(READ_FAILED_ON_THE_WAY, asked);
      throw thrown;
    });
  if (!r.ok) {
    repo.setError(errorText(r.errors), asked);
    return;
  }
  repo.setCustomers(r.value, asked);
}

/** One customer's scans at this counter. */
export function loadCustomerScans(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  customerId: string;
}): AsyncResult<Scan[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) =>
    o.client.scans.list(ctx, shopEntryId(o.shopId, o.locationId), { customerId: o.customerId, enableNoMatch: true }),
  );
}

/**
 * The customers' review queue at this counter: what each pending customer scan
 * groups with and how it differs from the card it matched. Not the
 * `reviewQueueRepository`, which holds the shop's own queue for Inventory.
 */
export function loadCustomerReviewQueue(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
}): AsyncResult<ScanReviewGroup[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) =>
    o.client.scans.reviewQueue(ctx, shopEntryId(o.shopId, o.locationId), undefined, "customer"),
  );
}

/**
 * A counter (POS) customer: staff scan and charge for them. Takes the plain store id; listed at the counter it was
 * made at, should the list still be that counter's when the answer lands.
 */
export async function createPosCustomer(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  name: string;
  customersRepository: CustomersRepository;
}): AsyncResult<StoreCustomer, ApiNotAuthorized | StoreNotFound | InvalidFormField> {
  const entry = shopEntryId(o.shopId, o.locationId);
  const r = await o.authHandler.callApi((ctx) => o.client.scans.createCustomer(ctx, o.shopId, o.name.trim()));
  if (r.ok) o.customersRepository.add(r.value, entry);
  return r;
}
